import ApplicationController from '../application_controller.js';

// Below Bootstrap's lg breakpoint the sidebar is a full-screen panel over the
// call (see #SidebarWrapper in custom-styles.scss) rather than a column
// beside it, so it starts closed and new messages don't pop it open.
const MOBILE_QUERY = '(max-width: 991.98px)';

export default class extends ApplicationController {
  static targets = ["sidebar", "attendeesView", "chatView", "attendeesTab", "chatTab", "pullTab", "chatButton", "unreadDot"];

  static values = {
    open: { type: Boolean, default: true },
    view: { type: String, default: "attendees" }
  };

  connect() {
    super.connect();
    this.unread = false;
    this.mobileQuery = window.matchMedia(MOBILE_QUERY);
    this.loadState();
    if (this.isMobile()) this.openValue = false;
    this.applyState();

    // Crossing the breakpoint (rotating a tablet, resizing a window)
    this.boundHandleBreakpoint = this.handleBreakpoint.bind(this);
    this.mobileQuery.addEventListener('change', this.boundHandleBreakpoint);

    // Listen for chat requesting sidebar to open
    this.boundHandleOpenRequest = this.handleOpenRequest.bind(this);
    document.addEventListener('live--chat:requestOpen', this.boundHandleOpenRequest);

    // Reapply state after StimulusReflex morphs (since sidebar content can be morphed)
    this.boundApplyState = this.applyState.bind(this);
    document.addEventListener('stimulus-reflex:after', this.boundApplyState);
    document.addEventListener('cable-ready:after-morph', this.boundApplyState);
  }

  disconnect() {
    super.disconnect();
    if (this.mobileQuery && this.boundHandleBreakpoint) {
      this.mobileQuery.removeEventListener('change', this.boundHandleBreakpoint);
    }
    if (this.boundHandleOpenRequest) {
      document.removeEventListener('live--chat:requestOpen', this.boundHandleOpenRequest);
    }
    if (this.boundApplyState) {
      document.removeEventListener('stimulus-reflex:after', this.boundApplyState);
      document.removeEventListener('cable-ready:after-morph', this.boundApplyState);
    }
  }

  isMobile() {
    return !!(this.mobileQuery && this.mobileQuery.matches);
  }

  handleBreakpoint() {
    if (this.isMobile()) {
      this.openValue = false;
    } else {
      this.loadState(); // back to the desktop open/closed preference
    }
    this.applyState();
  }

  // Participants have no tabs: their sidebar only ever shows chat
  isChatShowing() {
    return this.openValue && (this.viewValue === "chat" || !this.hasAttendeesTabTarget);
  }

  // Handle chat controller requesting sidebar to open (a message from someone else)
  handleOpenRequest() {
    if (this.isChatShowing()) return;
    if (this.isMobile()) {
      // A full-screen panel popping up over the call would be jarring; flag
      // the Chat button instead
      this.unread = true;
      this.applyState();
      return;
    }
    if (!this.openValue) {
      this.openValue = true;
      this.saveState();
      this.applyState();
    }
  }

  // Toggle sidebar open/close
  toggle() {
    this.openValue = !this.openValue;
    this.saveState();
    this.applyState();
  }

  // Open sidebar
  open() {
    this.openValue = true;
    this.saveState();
    this.applyState();
  }

  // Close sidebar (the close button on the mobile panel)
  close() {
    this.openValue = false;
    this.saveState();
    this.applyState();
  }

  // Show chat view (toggle if already showing chat). The Chat button in the
  // call controls.
  showChat() {
    if (this.isChatShowing()) {
      // Already open on chat - close sidebar
      this.openValue = false;
    } else {
      // Open and switch to chat
      this.openValue = true;
      this.viewValue = "chat";
    }
    this.saveState();
    this.applyState();
    if (this.isChatShowing()) this.scrollChatToBottom();
  }

  // Messages that arrived while the panel was hidden were added out of view
  scrollChatToBottom() {
    const messages = this.element.querySelector('[data-live--chat-target="messages"]');
    if (messages) messages.scrollTop = messages.scrollHeight;
  }

  // Show attendees view (toggle if already showing attendees)
  showAttendees() {
    if (this.openValue && this.viewValue === "attendees") {
      // Already open on attendees - close sidebar
      this.openValue = false;
    } else {
      // Open and switch to attendees
      this.openValue = true;
      this.viewValue = "attendees";
    }
    this.saveState();
    this.applyState();
  }

  // Switch view (for tab clicks)
  switchView(event) {
    event.preventDefault();
    const view = event.currentTarget.dataset.view;
    this.viewValue = view;
    this.saveState();
    this.applyState();
  }

  // Load state from localStorage
  loadState() {
    const saved = localStorage.getItem("sidebarState");
    if (saved) {
      try {
        const state = JSON.parse(saved);
        this.openValue = state.open ?? true;
        this.viewValue = state.view ?? "attendees";
      } catch (e) {
        // Invalid JSON, use defaults
      }
    }
  }

  // Save state to localStorage. On mobile the panel always starts closed, so
  // opening it there doesn't change the remembered desktop preference.
  saveState() {
    let open = this.openValue;
    if (this.isMobile()) {
      try {
        const saved = JSON.parse(localStorage.getItem("sidebarState") || "{}");
        open = saved.open ?? true;
      } catch (e) {
        open = true;
      }
    }
    try {
      localStorage.setItem("sidebarState", JSON.stringify({
        open: open,
        view: this.viewValue
      }));
    } catch (e) {
      // Storage unavailable (private mode); state just isn't remembered
    }
  }

  // Apply current state to DOM - CSS class toggle only, no inline styles
  applyState() {
    if (this.isChatShowing()) this.unread = false;

    // Sidebar visibility via CSS class. sidebar-open is what shows the mobile
    // panel; the server never renders it, so the panel can't flash open on
    // page load or during a morph before this runs.
    if (this.hasSidebarTarget) {
      this.sidebarTarget.classList.toggle("sidebar-closed", !this.openValue);
      this.sidebarTarget.classList.toggle("sidebar-open", this.openValue);
    }

    // Chat button in the call controls
    const chatShowing = this.isChatShowing();
    this.chatButtonTargets.forEach((button) => {
      button.classList.toggle("active", chatShowing);
      button.setAttribute("aria-expanded", chatShowing ? "true" : "false");
      button.setAttribute("aria-label", this.unread ? "Chat (new messages)" : (chatShowing ? "Close chat" : "Open chat"));
    });
    this.unreadDotTargets.forEach((dot) => dot.classList.toggle("d-none", !this.unread));

    // Tab/view visibility (only if targets exist - facilitators only)
    if (this.hasAttendeesViewTarget) {
      this.attendeesViewTarget.classList.toggle("d-none", this.viewValue === "chat");
    }
    // Only toggle chatView visibility when tabs exist (facilitators only)
    // Non-facilitators always see chat, no view switching
    if (this.hasChatViewTarget && this.hasAttendeesTabTarget) {
      this.chatViewTarget.classList.toggle("d-none", this.viewValue === "attendees");
    }
    if (this.hasAttendeesTabTarget) {
      this.attendeesTabTarget.classList.toggle("active", this.viewValue === "attendees");
    }
    if (this.hasChatTabTarget) {
      this.chatTabTarget.classList.toggle("active", this.viewValue === "chat");
    }

    // Update pull-tab icon direction
    if (this.hasPullTabTarget) {
      const icon = this.pullTabTarget.querySelector('i');
      if (icon) {
        icon.className = this.openValue ? 'bi bi-chevron-right' : 'bi bi-chevron-left';
      }
    }
  }
}
